import { useLazyQuery } from '@apollo/client';
import {
  Challenge,
  GET_USER_APPLIED_CHALLENGES,
  IntrapreneurChallenge,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ChallengeCardUser } from '@frontend/feature-intrapreneur-challenge';
import { Button, Empty, Spin } from 'antd';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

interface cursorBasedResponseType {
  edges: {
    cursor: string;
    node: IntrapreneurChallenge;
  }[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

function UserProfileChallenges() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const userId = router.query['id'] as string;
  const [autoFetch, setAutoFetch] = useState<boolean>(false);
  const [params, setParams] = useState<{
    userId: string;
    first: number;
    after?: string;
  }>({
    userId: userId,
    first: 6,
  });
  const [data, setData] = useState<cursorBasedResponseType>();

  const [getData, { loading }] = useLazyQuery<{
    userAppliedChallenge: cursorBasedResponseType;
  }>(GET_USER_APPLIED_CHALLENGES, {
    onCompleted: (res) => {
      const tempData = data?.edges || [];
      setData({
        edges: [...tempData, ...res.userAppliedChallenge.edges],
        pageInfo: res.userAppliedChallenge.pageInfo,
      });
    },
  });

  const handleLoadMore = () => {
    setParams({
      ...params,
      after: data?.edges[data.edges.length - 1].cursor,
    });
  };

  useEffect(() => {
    getData({
      variables: {
        ...params,
      },
    });
  }, [getData, params]);

  const Loader = () => {
    return (
      <div className="p-6 text-center">
        <Spin />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-gray-900 text-sm font-semibold">
        {t('challenges_participated')}
      </div>
      <InfiniteScroll
        dataLength={data?.edges.length || 0}
        next={handleLoadMore}
        hasMore={(autoFetch && data?.pageInfo.hasNextPage) || false}
        loader={<Loader />}
        className="lg:w-full w-full !overflow-visible"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-4 lg:gap-4 lg:grid-cols-3 w-full">
          {data?.edges.map((data, index) => (
            <ChallengeCardUser key={index} challenge={data.node as Challenge} />
          ))}
        </div>
      </InfiniteScroll>
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader />
        </div>
      ) : (
        data?.edges.length === 0 && (
          <div className="h-[200px] flex justify-center items-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )
      )}
      {!autoFetch && data?.pageInfo.hasNextPage && (
        <div className="text-center">
          <Button
            shape="round"
            loading={loading}
            onClick={() => setAutoFetch(true)}
          >
            {t('see_more')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default UserProfileChallenges;
